import React, { useState } from "react";
import { ArrowRight, Eye, EyeOff, LockKeyhole, MessageCircle, ShieldCheck } from "lucide-react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "../context/AuthContext";

const Login = () => {
  const { t } = useTranslation();
  const { login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [form, setForm] = useState({ email: "", password: "" });
  const [rememberMe, setRememberMe] = useState(() => localStorage.getItem("itontine_remember") === "true");
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const benefits = [[t("auth.secureMemberAccess"), LockKeyhole], [t("auth.memberSupport"), MessageCircle]];

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(form.email, form.password, rememberMe);
      navigate("/dashboard");
    } catch (err) {
      setError(err.response?.status === 401
        ? t("auth.loginError")
        : err.response?.status === 429
          ? t("auth.loginRateLimited")
          : err.response?.data?.message || t("common.error"));
      setShowForgotPassword(err.response?.status === 401);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="page-shell flex min-h-[calc(100vh-80px)] items-center justify-center">
      <div className="grid w-full max-w-5xl overflow-hidden rounded-3xl border border-gold-100 bg-white shadow-xl lg:grid-cols-[0.9fr_1.1fr]">
        <aside className="relative isolate flex flex-col justify-center overflow-hidden bg-gradient-to-br from-[#1f3b2c] via-[#17382c] to-[#102b22] p-7 text-white sm:p-10 lg:p-12">
          <svg aria-hidden="true" viewBox="0 0 200 200" className="pointer-events-none absolute right-4 top-4 h-28 w-28 text-gold-400 opacity-15 sm:right-6 sm:top-6 sm:h-36 sm:w-36">
            <circle cx="100" cy="100" r="78" fill="none" stroke="currentColor" strokeWidth="1.5" />
            <circle cx="100" cy="100" r="52" fill="none" stroke="white" strokeOpacity=".45" strokeWidth="1" strokeDasharray="3 7" />
            <path d="M100 22v26m0 104v26M22 100h26m104 0h26M45 45l19 19m72 72 19 19m0-110-19 19M64 136l-19 19" fill="none" stroke="currentColor" strokeWidth="1.5" />
            <circle cx="100" cy="22" r="5" fill="currentColor" />
            <circle cx="178" cy="100" r="5" fill="currentColor" />
            <circle cx="100" cy="178" r="5" fill="currentColor" />
            <circle cx="22" cy="100" r="5" fill="currentColor" />
            <circle cx="100" cy="100" r="12" fill="currentColor" fillOpacity=".28" stroke="currentColor" strokeWidth="1.5" />
          </svg>
          <div className="relative z-10">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-gold-400">{t("auth.welcomeBack")}</p>
            <h1 className="mt-4 max-w-md font-serif text-4xl font-bold leading-[1.08] sm:text-5xl">{t("auth.circleIsWaiting")}</h1>
            <p className="mt-4 max-w-md font-sans text-sm leading-6 text-green-100 sm:text-base sm:leading-7">{t("auth.loginIntro")}</p>

            <div className="mt-6 flex flex-wrap gap-x-5 gap-y-3">
              {benefits.map(([label, Icon]) => (
                <div className="flex items-center gap-2.5 text-sm font-medium text-white" key={label}>
                  <span className="flex h-8 w-8 items-center justify-center rounded-full border border-gold-400/35 bg-white/5 text-gold-300">
                    <Icon size={16} />
                  </span>
                  {label}
                </div>
              ))}
            </div>

          </div>
        </aside>
        <section className="p-6 sm:p-10">
          <div className="mb-8 flex items-center gap-3"><span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-gold-50 text-gold-600"><LockKeyhole size={22} /></span><div><p className="eyebrow">{t("auth.memberPortal")}</p><h2 className="font-serif text-3xl font-bold text-forest">{t("auth.loginTitle")}</h2></div></div>
          {searchParams.get("reason") === "session-expired" && <p role="status" className="mb-5 rounded-xl border border-amber-200 bg-amber-50 p-3 text-sm text-amber-900">{t("auth.sessionExpired")}</p>}
          {error && <p className="mb-5 rounded-xl border border-red-100 bg-red-50 p-3 text-sm text-red-700">{error}</p>}
          <form onSubmit={handleSubmit} className="auth-form space-y-5">
            <div><label className="label">{t("auth.email")}</label><input className="input !border-gray-200 !bg-white" type="email" required value={form.email} onChange={(event) => setForm({ ...form, email: event.target.value })} placeholder={t("common.emailPlaceholder")} /></div>
            <div><div className="flex items-center justify-between"><label className="label mb-2">{t("auth.password")}</label>{showForgotPassword && <Link to="/forgot-password" className="text-xs font-semibold text-gold-700 hover:underline">{t("auth.forgotPassword")}</Link>}</div><div className="relative"><input className="input !border-gray-200 !bg-white pr-11" type={showPassword ? "text" : "password"} required value={form.password} onChange={(event) => setForm({ ...form, password: event.target.value })} placeholder={t("common.passwordPlaceholder")} /><button type="button" onClick={() => setShowPassword((value) => !value)} className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" aria-label={t("common.passwordVisibility")}>{showPassword ? <EyeOff size={18} /> : <Eye size={18} />}</button></div></div>
            <label className="flex items-center gap-2 text-sm text-gray-600"><input type="checkbox" checked={rememberMe} onChange={(event) => setRememberMe(event.target.checked)} className="accent-gold-600" /> {t("common.rememberMe")}</label>
            <button className="btn-primary w-full" type="submit" disabled={submitting}>{submitting ? t("common.loading") : <>{t("common.signInSecurely")} <ArrowRight size={17} /></>}</button>
          </form>
          <div className="mt-6 flex items-center justify-center gap-2 text-xs text-gray-500"><ShieldCheck size={15} className="text-emerald-600" /> {t("common.secureAuthentication")}</div>
          <p className="mt-7 text-center text-sm text-gray-500">{t("auth.noAccount")} <Link to="/join" className="font-semibold text-gold-700 hover:underline">{t("nav.register")}</Link></p>
        </section>
      </div>
    </main>
  );
};

export default Login;
